import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, LayoutGrid, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ProductCard } from "@/components/ProductCard";
import { BrandTabs } from "@/components/BrandTabs";
import { FreeShippingNudge } from "@/components/FreeShippingNudge";
import { useShipping } from "@/hooks/useShipping";
import { useCategoryLabels } from "@/hooks/useCategoryLabels";
import {
  EmptyState,
  ErrorState,
  LoadingGrid,
  PageHeading,
} from "@/components/states";
import { useAuth } from "@/context/AuthContext";
import { groupByCategory, isDeliverableInCountry } from "@/lib/product";
import { brandCounts, productBrand } from "@/lib/brands";
import { useApiError } from "@/lib/useApiError";

const OVERIGE = "__overige__";

const Catalogus = () => {
  const { t } = useTranslation();
  const { producten, account } = useAuth();
  const verzending = useShipping();
  const toMessage = useApiError();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const merk = searchParams.get("merk") || "";
  const gekozen = useMemo(
    () => (searchParams.get("categorie") || "").split(",").map((c) => c.trim()).filter(Boolean),
    [searchParams],
  );

  const [term, setTerm] = useState(q);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterbarRef = useRef(null);
  const [barUitBeeld, setBarUitBeeld] = useState(false);

  useEffect(() => {
    const el = filterbarRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setBarUitBeeld(!e.isIntersecting), { rootMargin: "-80px 0px 0px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => setTerm(q), [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await producten(q);
      // Producten die het CRM niet levert in het land van deze klant tonen we niet.
      setItems(Array.isArray(data) ? data.filter((p) => isDeliverableInCountry(p, account)) : []);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producten, q, account]);

  useEffect(() => {
    load();
  }, [load]);

  const merken = useMemo(() => brandCounts(items), [items]);
  const gefilterd = useMemo(
    () => (merk ? items.filter((p) => productBrand(p)?.id === merk) : items),
    [items, merk],
  );
  const alleGroepen = useMemo(() => groupByCategory(items, t("catalog.otherCategory")), [items, t]);
  const categorieNamen = useMemo(
    () => alleGroepen.filter((g) => g.categorie !== OVERIGE).map((g) => g.categorie),
    [alleGroepen],
  );
  const label = useCategoryLabels(categorieNamen);
  const labelVoor = (groep) => (groep.categorie === OVERIGE ? groep.label : label(groep.categorie));

  /**
   * Eén sectie per merk (bij "alle merken"), elk met de eigen categorieën.
   * Zo blijven merken en categorieën gescheiden in plaats van door elkaar.
   */
  const secties = useMemo(() => {
    const maak = (id, naam, lijst) => ({
      id,
      naam,
      groepen: groupByCategory(lijst, t("catalog.otherCategory")).filter(
        (g) => gekozen.length === 0 || gekozen.includes(g.categorie),
      ),
    });
    if (merk) return [maak(merk, merken.find((m) => m.id === merk)?.naam || "", gefilterd)];
    const lijst = merken.map((m) =>
      maak(m.id, m.naam, items.filter((p) => productBrand(p)?.id === m.id)),
    );
    const zonderMerk = items.filter((p) => !productBrand(p));
    if (zonderMerk.length > 0) lijst.push(maak("__overig__", t("catalog.otherBrand"), zonderMerk));
    return lijst.filter((s) => s.groepen.length > 0);
  }, [merk, merken, items, gefilterd, gekozen, t]);

  /** Zijbalk: per merk de eigen categorieën (ongefilterd, zodat je kunt wisselen). */
  const merkCategorieen = useMemo(() => {
    const maak = (id, naam, lijst) => ({
      id,
      naam,
      groepen: groupByCategory(lijst, t("catalog.otherCategory")),
    });
    const lijst = merken.map((m) =>
      maak(m.id, m.naam, items.filter((p) => productBrand(p)?.id === m.id)),
    );
    const zonderMerk = items.filter((p) => !productBrand(p));
    if (zonderMerk.length > 0) lijst.push(maak("__overig__", t("catalog.otherBrand"), zonderMerk));
    return lijst.filter((s) => s.groepen.length > 0);
  }, [merken, items, t]);

  const zichtbaar = useMemo(
    () =>
      secties.reduce(
        (som, sec) => som + sec.groepen.reduce((n, g) => n + g.producten.length, 0),
        0,
      ),
    [secties],
  );

  // Standaard alle groepen uitgeklapt; handmatig in-/uitklappen blijft daarna staan.
  useEffect(() => {
    setOpen(secties.flatMap((sec) => sec.groepen.map((g) => `${sec.id}::${g.categorie}`)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merk, items]);

  const setParams = (next) => {
    const params = {};
    if (next.q ?? q) params.q = next.q ?? q;
    const gekozenMerk = next.merk ?? merk;
    if (gekozenMerk) params.merk = gekozenMerk;
    const categorieen = next.categorie ?? gekozen;
    if (categorieen.length > 0) params.categorie = categorieen.join(",");
    setSearchParams(params, { replace: true });
  };

  // Elk merk heeft eigen categorieën; bij wisselen vervalt de categoriekeuze.
  const kiesMerk = (id) => setParams({ merk: id === merk ? "" : id, categorie: [] });

  /** Categorie aanvinken binnen een merk: het merk wordt dan ook de actieve keuze. */
  const kiesCategorie = (merkId, categorie) => {
    if (merk && merk !== merkId) {
      setParams({ merk: merkId, categorie: [categorie] });
      return;
    }
    const next = gekozen.includes(categorie)
      ? gekozen.filter((c) => c !== categorie)
      : [...gekozen, categorie];
    setParams({ merk: next.length > 0 ? merkId : merk, categorie: next });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setParams({ q: term.trim() });
  };

  const clearSearch = () => {
    setTerm("");
    setParams({ q: "" });
  };

  const actieveFilters = (q ? 1 : 0) + (merk ? 1 + gekozen.length : 0);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="catalog-page">
      <PageHeading title={t("catalog.title")} testId="catalog-heading" />

      <div className="hidden sm:block"><FreeShippingNudge verzending={verzending} testId="catalog-free-shipping-nudge" /></div>

      <div ref={filterbarRef} className="mt-6 flex flex-wrap items-center justify-between gap-3" data-testid="catalog-filterbar">
        <BrandTabs merken={merken} actief={merk} onKies={kiesMerk} testId="catalog-brand-quick" />
        <div className="ml-auto flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => setFilterOpen(true)} data-testid="catalog-filter-button">
          <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("catalog.filters")}
          {actieveFilters > 0 && (
            <span className="ml-2 rounded-full bg-foreground px-1.5 text-[11px] font-semibold text-background" data-testid="catalog-filter-count">{actieveFilters}</span>
          )}
        </Button>
        {q && (
          <button type="button" onClick={clearSearch} className="inline-flex items-center gap-1 rounded-full border bg-secondary px-3 py-1.5 text-xs" data-testid="catalog-chip-search">
            <Search className="h-3 w-3" aria-hidden="true" />“{q}”<X className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
        {merk && gekozen.map((cat) => (
          <button key={cat} type="button" onClick={() => kiesCategorie(merk, cat)} className="inline-flex items-center gap-1 rounded-full border bg-secondary px-3 py-1.5 text-xs" data-testid={`catalog-chip-${cat === OVERIGE ? "overige" : cat.toLowerCase().replace(/\s+/g, "-")}`}>
            {labelVoor({ categorie: cat, label: t("catalog.otherCategory") })}<X className="h-3 w-3" aria-hidden="true" />
          </button>
        ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setFilterOpen(true)}
        aria-label={t("catalog.filters")}
        className={`fixed bottom-6 right-4 z-40 inline-flex h-14 items-center gap-2 rounded-full bg-primary px-5 font-semibold text-primary-foreground shadow-[0_10px_30px_-8px_hsl(var(--primary)/0.6)] transition-[opacity,transform] duration-300 hover:scale-105 sm:right-8 ${
          barUitBeeld && !filterOpen ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
        }`}
        data-testid="catalog-filter-fab"
      >
        <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
        <span className="hidden sm:inline">{t("catalog.filters")}</span>
        {actieveFilters > 0 && (
          <span className="rounded-full bg-background px-2 py-0.5 text-xs font-bold text-foreground" data-testid="catalog-filter-fab-count">{actieveFilters}</span>
        )}
      </button>

      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="catalog-filter-sheet">
          <SheetHeader className="text-left">
            <SheetTitle>{t("catalog.filters")}</SheetTitle>
            <SheetDescription className="sr-only">{t("catalog.searchLabel")}</SheetDescription>
          </SheetHeader>
          <div className="mt-5">
          <form onSubmit={onSubmit} className="w-full">
            <Label htmlFor="catalog-search" className="sr-only">
              {t("catalog.searchLabel")}
            </Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  id="catalog-search"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={t("catalog.searchPlaceholder")}
                  className="pl-9"
                  data-testid="catalog-search-input"
                />
              </div>
              <Button type="submit" data-testid="catalog-search-button">
                {t("catalog.search")}
              </Button>
              {q && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={t("catalog.clear")}
                  onClick={clearSearch}
                  data-testid="catalog-clear-search-button"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t("catalog.searchHint")}</p>
          </form>
          </div>
          <div className="mt-6" data-testid="catalog-category-nav">

            <p className="flex items-center gap-2 text-sm font-semibold">
              <LayoutGrid className="h-4 w-4 text-primary" aria-hidden="true" />
              {t("catalog.categories")}
            </p>
            {merk && (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="catalog-category-brand-scope">
                {t("catalog.withinBrand", { merk: merken.find((m) => m.id === merk)?.naam || "" })}
              </p>
            )}

            <div className="mt-3 space-y-1">
              <button
                type="button"
                onClick={() => setParams({ categorie: [] })}
                className={`w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                  gekozen.length === 0 ? "bg-secondary font-medium text-foreground" : "text-muted-foreground hover:bg-secondary"
                }`}
                data-testid="catalog-category-all"
              >
                {t("catalog.allCategories")} ({gefilterd.length})
              </button>
            </div>

            <div className="mt-4 space-y-5">
              {merkCategorieen.map((sec) => (
                <div key={sec.id} data-testid={`catalog-category-brand-${sec.id}`}>
                  <p
                    className={`px-2 text-[11px] font-semibold uppercase tracking-widest transition-colors ${
                      merk && merk !== sec.id ? "text-muted-foreground" : "text-primary"
                    }`}
                  >
                    {sec.naam}
                  </p>
                  <div className="mt-1.5 space-y-1 border-l pl-2">
                    {sec.groepen.map((g) => (
                      <label
                        key={g.categorie}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-secondary"
                        data-testid={`catalog-category-option-${sec.id}-${g.categorie === OVERIGE ? "overige" : g.categorie.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Checkbox
                          checked={merk === sec.id && gekozen.includes(g.categorie)}
                          onCheckedChange={() => kiesCategorie(sec.id, g.categorie)}
                          aria-label={`${sec.naam} — ${labelVoor(g)}`}
                        />
                        <span className="min-w-0 flex-1 truncate">{labelVoor(g)}</span>
                        <span className="text-xs text-muted-foreground">{g.producten.length}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
                    </div>
          <Button className="mt-6 w-full" onClick={() => setFilterOpen(false)} data-testid="catalog-filter-apply">
            {t("catalog.showResults", { count: zichtbaar })}
          </Button>
        </SheetContent>
      </Sheet>

      <div className="mt-6 grid gap-6 lg:grid-cols-12">

        <div className="lg:col-span-12">
          {loading ? (
            <LoadingGrid />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : zichtbaar === 0 ? (
            <EmptyState
              title={q ? t("catalog.empty", { q }) : t("catalog.emptyAll")}
              actionLabel={q ? t("catalog.emptyAction") : undefined}
              onAction={q ? clearSearch : undefined}
            />
          ) : (
            <>
              <p className="text-sm text-muted-foreground" data-testid="catalog-result-count">
                {q
                  ? t("catalog.resultsFor", { count: zichtbaar, q })
                  : t("catalog.results", { count: zichtbaar })}
              </p>

              <div className="mt-4 space-y-8">
                {secties.map((sec) => (
                  <section key={sec.id} data-testid={`catalog-brand-section-${sec.id}`}>
                    {secties.length > 1 && (
                      <div className="mb-3 flex items-baseline gap-3 border-b pb-2">
                        <h2 className="font-display text-lg font-semibold tracking-tight">{sec.naam}</h2>
                        <span className="text-xs uppercase tracking-widest text-muted-foreground">
                          {t("catalog.results", {
                            count: sec.groepen.reduce((n, g) => n + g.producten.length, 0),
                          })}
                        </span>
                      </div>
                    )}
                    <Accordion
                      type="multiple"
                      value={open}
                      onValueChange={setOpen}
                      className="space-y-4"
                      data-testid={`catalog-category-accordion-${sec.id}`}
                    >
                      {sec.groepen.map((g) => (
                        <AccordionItem
                          key={g.categorie}
                          value={`${sec.id}::${g.categorie}`}
                          className="overflow-hidden rounded-2xl border bg-card/60 px-5"
                          data-testid={`catalog-group-${g.categorie === OVERIGE ? "overige" : g.categorie.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <AccordionTrigger className="py-4 hover:no-underline">
                            <span className="flex items-baseline gap-3 text-left">
                              <span className="font-display text-base font-semibold">{labelVoor(g)}</span>
                              <span className="text-xs text-muted-foreground">
                                {t("catalog.categoryCount", { count: g.producten.length })}
                              </span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="pb-6">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 lg:gap-6" data-testid="catalog-grid">
                              {g.producten.map((product) => (
                                <ProductCard key={product.id} product={product} />
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Catalogus;
